
import { Options, Vue } from "vue-class-component";
import { AuthController } from "@/app/ui/controllers/AuthController";
import { removeChallengeToken } from "@/app/infrastructures/misc/Cookies";
import { PHONE_NUMBER_HD } from '@/app/infrastructures/misc/Constants';
import { cleanPhoneNumber } from '@/app/infrastructures/misc/common-library/Whatsapp';
import { AccountController } from '../../controllers/AccountController';

@Options({
  name: "VerifyOtpPage"
})
export default class VerifyOtpPage extends Vue {
  otpDigits: string[] = Array(6).fill("");
  countdown = 0;
  countdownTimer: ReturnType<typeof setInterval> | null = null;
  cleanPhoneNumber = cleanPhoneNumber;

  get phoneNumberHD() {
    return PHONE_NUMBER_HD;
  }

  get isLoading() {
    return AuthController.isLoading;
  }

  get maskedEmail() {
    return AuthController.login2faMaskedEmail;
  }

  get errorMessage() {
    return AuthController.login2faOtpErrorMessage;
  }

  get hasError() {
    return AuthController.login2faOtpHasError;
  }

  get isOtpComplete() {
    return this.otpDigits.every(d => d.length === 1);
  }

  get countdownDisplay() {
    const m = Math.floor(this.countdown / 60);
    const s = this.countdown % 60;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }

  created() {
    if (!AuthController.login2faSessionToken) {
      this.$router.replace("/");
      return;
    }
    this.startCountdown(AuthController.login2faResendAvailableInSeconds);
  }

  unmounted() {
    if (this.countdownTimer) clearInterval(this.countdownTimer);
  }

  startCountdown(seconds: number) {
    if (this.countdownTimer) clearInterval(this.countdownTimer);
    this.countdown = seconds;
    if (seconds <= 0) return;
    this.countdownTimer = setInterval(() => {
      this.countdown--;
      if (this.countdown <= 0 && this.countdownTimer) {
        clearInterval(this.countdownTimer);
        this.countdownTimer = null;
      }
    }, 1000);
  }

  getInputEl(index: number): HTMLInputElement | null {
    const ref = this.$refs[`otpInput${index}`];
    if (Array.isArray(ref)) return ref[0] as HTMLInputElement;
    return ref as HTMLInputElement ?? null;
  }

  onInput(event: Event, index: number) {
    const input = event.target as HTMLInputElement;
    const val = input.value.replace(/\D/g, "").slice(-1);
    this.otpDigits[index] = val;
    input.value = val;
    AuthController.setLogin2faOtpHasError(false);
    AuthController.setLogin2faOtpErrorMessage("");
    if (val && index < 5) {
      this.$nextTick(() => this.getInputEl(index + 1)?.focus());
    }
  }

  onKeyDown(event: KeyboardEvent, index: number) {
    if (event.key === "Backspace" && !this.otpDigits[index] && index > 0) {
      this.$nextTick(() => this.getInputEl(index - 1)?.focus());
    }
    if (event.key === "ArrowLeft" && index > 0) {
      event.preventDefault();
      this.getInputEl(index - 1)?.focus();
    }
    if (event.key === "ArrowRight" && index < 5) {
      event.preventDefault();
      this.getInputEl(index + 1)?.focus();
    }
  }

  onPaste(event: ClipboardEvent) {
    event.preventDefault();
    const text = event.clipboardData?.getData("text") ?? "";
    const digits = text.replace(/\D/g, "").slice(0, 6).split("");
    digits.forEach((d, i) => {
      this.otpDigits[i] = d;
      const el = this.getInputEl(i);
      if (el) el.value = d;
    });
  }

  async verify() {
    if (!this.isOtpComplete || this.isLoading) return;
    await AuthController.verifyLoginOtp(this.otpDigits.join(""));
  }

  async resend() {
    if (this.isLoading) return;
    await AuthController.resendLoginOtp();
    this.startCountdown(AuthController.login2faResendAvailableInSeconds);
    this.otpDigits = Array(6).fill("");
    this.$nextTick(() => this.getInputEl(0)?.focus());
  }

  goBack() {
    removeChallengeToken();
    AuthController.clearLogin2faOtpState();
    this.$router.replace("/");
  }

  get isCountryCodeEmpty() {
    return !AccountController.accountData.countryCode;
  }
}
